import { useEffect, useRef, useState } from 'react';
import { project, greatCirclePoints, arcToPolylines } from '../utils/arcUtils';

interface Props {
  userLat: number;
  userLon: number;
  userLabel: string;
  colleagueLat: number;
  colleagueLon: number;
  colleagueLabel: string;
}

type Ring = number[][];

function ringToPath(ring: Ring): string {
  return ring
    .map(([lon, lat], i) => {
      const [x, y] = project(lon, lat);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ') + ' Z';
}

function featuresToPaths(features: GeoJSON.Feature[]): string[] {
  const paths: string[] = [];
  for (const f of features) {
    const g = f.geometry as GeoJSON.Geometry;
    if (!g) continue;
    if (g.type === 'Polygon') {
      paths.push(ringToPath(g.coordinates[0] as Ring));
    } else if (g.type === 'MultiPolygon') {
      for (const poly of g.coordinates) {
        paths.push(ringToPath(poly[0] as Ring));
      }
    }
  }
  return paths;
}

let cachedPaths: string[] | null = null;
let loadPromise: Promise<string[]> | null = null;

function loadPaths(): Promise<string[]> {
  if (cachedPaths) return Promise.resolve(cachedPaths);
  if (loadPromise) return loadPromise;
  loadPromise = fetch('/world-countries.json')
    .then(r => r.json())
    .then((data: GeoJSON.FeatureCollection) => {
      cachedPaths = featuresToPaths(data.features);
      return cachedPaths;
    });
  return loadPromise;
}

export default function MeetingMap({ userLat, userLon, userLabel, colleagueLat, colleagueLon, colleagueLabel }: Props) {
  const [paths, setPaths] = useState<string[] | null>(cachedPaths);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!cachedPaths) {
      loadPaths().then(p => { if (mounted.current) setPaths(p); });
    }
    return () => { mounted.current = false; };
  }, []);

  const W = 960;
  const H = 480;
  const VIEW_TOP = 53;
  const VIEW_BOTTOM = 400;
  const VIEW_H = VIEW_BOTTOM - VIEW_TOP;

  const [userX, userY] = project(userLon, userLat);
  const clampedUserX = Math.max(4, Math.min(W - 4, userX));
  const clampedUserY = Math.max(VIEW_TOP + 4, Math.min(VIEW_BOTTOM - 4, userY));

  const [colX, colY] = project(colleagueLon, colleagueLat);
  const clampedColX = Math.max(4, Math.min(W - 4, colX));
  const clampedColY = Math.max(VIEW_TOP + 4, Math.min(VIEW_BOTTOM - 4, colY));

  const arcLines = arcToPolylines(greatCirclePoints(userLon, userLat, colleagueLon, colleagueLat));

  const userLabelX = clampedUserX;
  const userLabelY = clampedUserY - 20;
  const colLabelX = clampedColX;
  const colLabelY = clampedColY - 20;

  return (
    <svg
      viewBox={`0 ${VIEW_TOP} ${W} ${VIEW_H}`}
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      aria-hidden="true"
    >
      {paths && (
        <g fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5">
          {paths.map((d, i) => <path key={i} d={d} />)}
        </g>
      )}

      {arcLines.map((pts, i) => (
        <polyline
          key={i}
          points={pts}
          fill="none"
          stroke="rgba(56,189,163,0.8)"
          strokeWidth="2"
          strokeDasharray="8 6"
          strokeLinecap="round"
        />
      ))}

      {/* User pin */}
      <g transform={`translate(${clampedUserX},${clampedUserY})`}>
        <circle r="16" fill="rgba(56,189,163,0.15)" />
        <circle r="10" fill="rgba(56,189,163,0.9)" />
        <circle r="4" fill="white" />
      </g>
      <text
        x={userLabelX}
        y={userLabelY}
        textAnchor="middle"
        fill="rgba(255,255,255,0.85)"
        fontSize="11"
        fontWeight="500"
      >
        {userLabel}
      </text>

      {/* Colleague pin */}
      <g transform={`translate(${clampedColX},${clampedColY})`}>
        <circle r="16" fill="rgba(245,158,66,0.15)" />
        <circle r="10" fill="rgba(245,158,66,0.9)" />
        <circle r="4" fill="white" />
      </g>
      <text
        x={colLabelX}
        y={colLabelY}
        textAnchor="middle"
        fill="rgba(255,255,255,0.85)"
        fontSize="11"
        fontWeight="500"
      >
        {colleagueLabel}
      </text>
    </svg>
  );
}
